import React, {useEffect, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {Archive} from '../types/Archive';
import {Publication} from '../types/Publication';
import archivesService from '../services/archivesService';
import publicationsService from '../services/publicationsService';
import {useNavigate, useParams} from 'react-router-dom';
import AdminFormHeader from "../../../layout/admin/AdminFormHeader";
import {AdminButton, AdminForm, AdminFormSection, AdminInputDropdown, AdminInputText} from "../../../layout/admin";
import {useFormErrorHandling} from "../../../common/hooks/useFormErrorHandling";

const ArchiveForm: React.FC = () => {
  const {t} = useTranslation();
  const [formData, setFormData] = useState({
    abbreviation: '',
    name: '',
    publicationId: ''
  });
  const [archive, setArchive] = useState<Archive | null>(null);
  const [publications, setPublications] = useState<Publication[]>([]);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const {id} = useParams<{ id: string }>();
  const isEditMode = Boolean(id);
  const {errors, handleApiError, clearErrors} = useFormErrorHandling();

  useEffect(() => {
    fetchPublications();
    if (isEditMode && id) {
      fetchArchive(id);
    }
  }, [id, isEditMode]);

  const fetchPublications = async (): Promise<void> => {
    const publicationsData = await publicationsService.getAllPublications();
    setPublications(publicationsData);
  };

  const fetchArchive = async (archiveId: string): Promise<void> => {
    setLoading(true);
    try {
      const archiveData = await archivesService.getArchive(archiveId);
      setArchive(archiveData);
      setFormData({
        abbreviation: archiveData.abbreviation,
        name: archiveData.name,
        publicationId: archiveData.publication?.id || ''
      });
    } catch (error) {
      console.error('Error fetching archive:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field: string, value: string): void => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    setLoading(true);
    clearErrors();

    try {
      if (isEditMode && archive) {
        const updatedArchive: Archive = {
          id: archive.id,
          abbreviation: formData.abbreviation,
          name: formData.name,
          publication: formData.publicationId
            ? {
              id: formData.publicationId,
              name: publications.find(p => p.id === formData.publicationId)?.name || ''
            }
            : undefined
        };
        await archivesService.updateArchive(updatedArchive);
      } else {
        await archivesService.createArchive({
          abbreviation: formData.abbreviation,
          name: formData.name,
          publication: formData.publicationId
            ? {
              id: formData.publicationId,
              name: publications.find(p => p.id === formData.publicationId)?.name || ''
            }
            : undefined
        });
      }
      navigate('/archives');
    } catch (error) {
      handleApiError(error);
    } finally {
      setLoading(false);
    }
  };

  const publicationOptions = [
    {label: '-- None --', value: ''},
    ...publications.map(pub => ({
      label: pub.name,
      value: pub.id
    }))
  ];

  return (
    <>
      <AdminFormHeader
        title={archive ? t('admin.archives.title.edit') : t('admin.archives.title.create')}
        subtitle={archive ? t('admin.archives.subtitle.edit') : t('admin.archives.subtitle.create')}
      />

      <AdminForm
        leftColumn={
          <>
            <AdminFormSection title={t('admin.common.sections.basicInfo')}>
              <AdminInputText
                value={formData.abbreviation}
                onChange={(e) => handleInputChange('abbreviation', e.target.value)}
                name="abbreviation"
                id="abbreviation"
                label={t('admin.archives.fields.abbreviation')}
                required={true}
                error={errors.abbreviation}
              />

              <AdminInputText
                value={formData.name}
                onChange={(e) => handleInputChange('name', e.target.value)}
                name="name"
                id="name"
                label={t('admin.common.fields.name')}
                required={true}
                error={errors.name}
              />

              <AdminInputDropdown
                value={formData.publicationId}
                onChange={(e) => handleInputChange('publicationId', e.target.value)}
                name="publicationId"
                id="publicationId"
                label={t('admin.archives.fields.publication')}
                options={publicationOptions}
                error={errors.publication}
              />
            </AdminFormSection>
          </>
        }
        formActions={
          <>
            <AdminButton
              type="button"
              label={t('admin.common.actions.cancel')}
              onClick={() => navigate("/archives")}
            />
            <AdminButton
              type="submit"
              label={archive ? t('admin.common.actions.update') : t('admin.common.actions.create')}
              variant="black"
              disabled={loading}
            />
          </>
        }
        onSubmit={handleSubmit}
      />
    </>
  );
};

export default ArchiveForm;


